import { useCallback, useMemo } from 'react';
import {
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { queryKeys } from '@/services/query-keys';
import {
  tradesService,
  type RegistryQueryParams,
  type TabBook,
} from '@/services/trades/trades-service';
import type { Counterparty } from '@/types';

const ACTIVE_PARAMS = { is_active: true } as const;

const REGISTRY_QUERY = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  // The registry is near-static; don't refetch on window focus (parity with the other
  // reference hooks). A focus-triggered refetch that fails would otherwise flip the
  // query to `error` while its data is still cached.
  refetchOnWindowFocus: false,
} as const;

interface EnabledOption {
  /** Skip fetching until true — lets the always-mounted trade modal defer the
   *  load until it actually opens instead of firing on every page. */
  enabled?: boolean;
}

/**
 * The active counterparties, for a picker that lists them all (the trade form). The registry
 * runs to tens of thousands of rows, nearly all of them deactivated, so no screen loads it
 * whole: tabs come from `useCounterpartyTabs`, a name from `useCounterpartyIdsByName`, the
 * rest from the server search.
 */
export const useCounterparties = ({ enabled = true }: EnabledOption = {}) =>
  useQuery({
    queryKey: queryKeys.trades.counterparties(ACTIVE_PARAMS),
    queryFn: () => tradesService.getAllCounterparties(ACTIVE_PARAMS),
    ...REGISTRY_QUERY,
    enabled,
  });

const TAB_PAGE_SIZE = 50;

/**
 * The counterparties that get a tab on `book`'s page (the active ones and the deactivated
 * ones with a book there), by name, a page at a time: the first page feeds the
 * tab row, the rest is paged into the "+N more" menu. `total` is the whole list's size.
 */
export const useCounterpartyTabs = (book: TabBook) => {
  const query = useInfiniteQuery({
    queryKey: queryKeys.trades.counterpartyTabs(book),
    initialPageParam: 0,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      tradesService.getCounterpartyTabs(book, TAB_PAGE_SIZE, pageParam),
    // A row cursor, and an empty page ends the paging whatever `count` says (see
    // useCounterpartySearch).
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.results.length === 0) return undefined;
      const loaded = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return loaded < lastPage.count ? loaded : undefined;
    },
    ...REGISTRY_QUERY,
  });
  const pages = query.data?.pages;
  const rows = useMemo(() => {
    // Deduplicated by id: offset paging under a changing order can hand a row back twice.
    const byId = new Map<number, Counterparty>();
    for (const page of pages ?? []) for (const cp of page.results) if (!byId.has(cp.id)) byId.set(cp.id, cp);
    return [...byId.values()];
  }, [pages]);
  return {
    ...query,
    /** The tab row's rows: the first page, in server order. */
    firstPage: pages?.[0]?.results,
    /** Every row loaded so far. */
    rows,
    total: pages?.[0]?.count ?? 0,
  };
};

export interface CounterpartyIdsByName {
  /** Only the names that resolved; a missing one is unknown to the registry. */
  nameToId: Record<string, number>;
  /** A lookup is still in flight, with no earlier answer to serve meanwhile. */
  isPending: boolean;
  /** A lookup failed with no earlier answer to fall back on. */
  error: Error | null;
}

const NAMES_PER_LOOKUP = 50;
// Encoded `name_exact=` characters a request carries, well under the 8 KB request line nginx
// allows by default: 50 names of the full 255 characters would not fit.
const LOOKUP_URL_BUDGET = 4000;

/**
 * What earlier lookups answered for `batch`, if each of its names was in one of them (an
 * absent name was looked up and is unknown). A batch changes when a name leaves the filter
 * or a tab page resolves one, and the ids it needs are already known.
 */
const answeredBefore = (queryClient: QueryClient, batch: string[]): Counterparty[] | undefined => {
  const wanted = new Set(batch);
  const asked = new Set<string>();
  const rows: Counterparty[] = [];
  const paramsAt = queryKeys.trades.counterparties().length;
  for (const [key, data] of queryClient.getQueriesData<Counterparty[]>({
    queryKey: queryKeys.trades.counterparties(),
  })) {
    const names = (key[paramsAt] as RegistryQueryParams | undefined)?.name_exact;
    if (!Array.isArray(names) || data === undefined) continue;
    for (const name of names) asked.add(name);
    for (const cp of data) if (wanted.has(cp.name)) rows.push(cp);
  }
  return batch.every((name) => asked.has(name)) ? rows : undefined;
};

/**
 * Ids for counterparties the caller knows only by name (the sidebar filter and saved views
 * keep names), resolved by exact name, up to `NAMES_PER_LOOKUP` names (or `LOOKUP_URL_BUDGET`) a
 * request. Pass only
 * the names no loaded list resolved.
 */
export const useCounterpartyIdsByName = (
  names: readonly string[],
  { enabled = true }: EnabledOption = {},
): CounterpartyIdsByName => {
  const queryClient = useQueryClient();
  // Sorted, so the same names make the same requests. A blank name names no counterparty.
  const batches = useMemo(() => {
    const wanted = [...new Set(names)].filter((name) => name.trim() !== '').sort();
    const out: string[][] = [];
    let size = 0;
    for (const name of wanted) {
      const cost = encodeURIComponent(name).length + '&name_exact='.length;
      const last = out[out.length - 1];
      if (last && last.length < NAMES_PER_LOOKUP && size + cost <= LOOKUP_URL_BUDGET) {
        last.push(name);
        size += cost;
      } else {
        out.push([name]);
        size = cost;
      }
    }
    return out;
  }, [names]);
  const combine = useCallback(
    (results: UseQueryResult<Counterparty[]>[]): CounterpartyIdsByName => {
      const nameToId: Record<string, number> = {};
      for (const result of results) {
        for (const cp of result.data ?? []) nameToId[cp.name] = cp.id;
      }
      return {
        nameToId,
        isPending: enabled && results.some((r) => r.isPending),
        // A failed refetch keeps the id it resolved before: only a lookup with nothing
        // cached is a failure.
        error: results.find((r) => r.error && r.data === undefined)?.error ?? null,
      };
    },
    [enabled],
  );
  return useQueries({
    queries: batches.map((batch) => ({
      queryKey: queryKeys.trades.counterparties({ name_exact: batch }),
      queryFn: () => tradesService.getAllCounterparties({ name_exact: batch }),
      // Not `keepPreviousData`: `useQueries` gives a new key a new observer, with no previous data.
      placeholderData: () => answeredBefore(queryClient, batch),
      ...REGISTRY_QUERY,
      enabled,
    })),
    combine,
  });
};
